// Books: buy, support, read link, library and the private PDF upload (room-stc /book/*, file-stc PRIVATE).
import client from "./client";
import { call, getUserPostsByType, type BookInfo, type ContentId, type Page, type PostV2, normalizePage, normalizePost } from "./content";
import { uploadFile, type UploadOptions } from "./upload";
import type { FileInfo } from "./files";

export type { BookInfo, BookInput } from "./content";

export type BookOwned = "BOUGHT" | "SUPPORTED";
export interface BookPurchase { owned: BookOwned; paymentId: string }
export interface BookReadLink { url: string; expiresAt: string; fileId?: string }

export const buyBook = (postId: ContentId) =>
  call<BookPurchase>(client.post(`/api/content/v2/posts/${postId}/book/buy`));

export const supportBook = (postId: ContentId, amountCents: number) =>
  call<BookPurchase>(client.post(`/api/content/v2/posts/${postId}/book/support`, { amountCents }));

/** A short-lived signed link to the private PDF (author, BOUGHT viewer, or a free book). */
export const getBookReadLink = (postId: ContentId) =>
  call<BookReadLink>(client.get(`/api/content/v2/posts/${postId}/book/read-link`));

/** Books the viewer bought or supported. */
export const getLibrary = (cursor?: string | null, limit = 20) =>
  call(
    client.get("/api/content/v2/users/me/library", { params: { cursor: cursor || undefined, limit } }),
    normalizePage(normalizePost) as (v: unknown) => Page<PostV2>,
  );

export const getUserBooks = (userId: string, cursor?: string | null, limit = 20) =>
  getUserPostsByType(userId, "BOOK", cursor, limit);

/** Support rule (R11): only on a free book, or after the viewer BOUGHT it; never the author's own book. */
export function canSupport(book: Pick<BookInfo, "priceCents" | "owned">, isAuthor: boolean): boolean {
  if (isAuthor) return false;
  const free = book.priceCents == null || book.priceCents === 0;
  return free || book.owned === "BOUGHT";
}

/** Uploads a book's PDF as a PRIVATE file (no public URL; read through getBookReadLink). */
export const uploadBookPdf = (file: File, opts: UploadOptions = {}): Promise<FileInfo> =>
  uploadFile(file, { filename: file.name, mime: "application/pdf", visibility: "PRIVATE" }, opts);
